import React, { FC, useEffect, useState } from "react"
import { Form, Input, InputNumber, Switch, Modal, Space, Button, Typography, Popover } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { observer } from "mobx-react"
import { notifications } from "notifications"
import { SubmitCancelButtons, GameSelect } from "components"
import { useStore } from "stores/utils/use-store"
import { Formula } from "../store/types"
import { RESERVED_WORDS } from "../constants"
import TextArea from "antd/lib/input/TextArea"
import { validateFormula } from "../api/formulas.call"

const { Text } = Typography

interface CreateEditFormulaModalProps {
  visible: boolean
  onClose: () => void
  initialData?: Formula | null
}

export const CreateEditFormulaModal: FC<CreateEditFormulaModalProps> = observer(({
  visible,
  onClose,
  initialData,
}) => {
  const { dataStore: { formulasStore } } = useStore()
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const [validating, setValidating] = useState(false)
  const [validationResult, setValidationResult] = useState<{ success: boolean; message: string } | null>(null)

  const applyMinMaxClamp = Form.useWatch("applyMinMaxClamp", form)
  const applyBalancer = Form.useWatch("applyBalancer", form)
  const applyVictoryBonus = Form.useWatch("applyVictoryBonus", form)

  useEffect(() => {
    if (visible) {
      if (initialData) {
        form.setFieldsValue({ ...initialData })
      } else {
        form.resetFields()
      }
      setValidationResult(null)
    }
  }, [visible, initialData, form])

  const onFinishHandler = async(values: any) => {
    if (submitting) return

    const data = {
      gameId: Number(values.gameId),
      gameMode: values.gameMode && values.gameMode.trim() ? values.gameMode.trim() : null,
      formula: values.formula,
      applyMinMaxClamp: values.applyMinMaxClamp || false,
      minReward: values.applyMinMaxClamp ? values.minReward : undefined,
      maxReward: values.applyMinMaxClamp ? values.maxReward : undefined,
      applyBalancer: values.applyBalancer || false,
      balancer: values.applyBalancer ? values.balancer : undefined,
      applyVictoryBonus: values.applyVictoryBonus || false,
      victoryBonusValue: values.applyVictoryBonus ? values.victoryBonusValue : undefined,
      premiumBoosterDisabled: values.premiumBoosterDisabled || false,
      isActive: values.isActive !== undefined ? values.isActive : true,
    }

    setSubmitting(true)

    try {
      if (initialData) {
        await formulasStore.updateFormula(initialData.id, data)
        notifications.success("Formula updated")
      } else {
        await formulasStore.createFormula(data)
        notifications.success("Formula created")
      }
      setSubmitting(false)
      onClose()
      form.resetFields()
    } catch (error: any) {
      notifications.error(error?.cause?.message || error?.message || "Failed to save formula")
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    form.resetFields()
    setValidationResult(null)
    onClose()
  }

  const handleValidateFormula = async() => {
    const formulaValue = form.getFieldValue("formula")
    if (!formulaValue || !formulaValue.trim()) {
      notifications.error("Please enter a formula first")

      return
    }

    setValidating(true)
    setValidationResult(null)
    try {
      const result = await validateFormula(formulaValue)
      setValidationResult(result)
    } catch (error: any) {
      setValidationResult({
        success: false,
        message: error?.cause?.message || error?.message || "Failed to validate formula",
      })
    } finally {
      setValidating(false)
    }
  }

  const functionsPopoverContent = (
    <div style={{ maxWidth: "300px" }}>
      <Text strong>Available Functions:</Text>
      <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: "4px" }}>
        {RESERVED_WORDS.map(func => (
          <code
            key={func}
            style={{
              padding: "2px 6px",
              background: "#f5f5f5",
              borderRadius: "3px",
              fontSize: "12px",
            }}
          >
            {func}
          </code>
        ))}
      </div>
    </div>
  )

  return (
    <Modal
      open={visible}
      title={initialData ? "Edit Formula" : "Create Formula"}
      width="700px"
      footer={null}
      onCancel={handleClose}
      style={{ top: "10px" }}
    >
      <Form
        form={form}
        onFinish={onFinishHandler}
        layout="vertical"
        id="createEditFormula"
      >
        <Form.Item
          label="Is Active"
          name="isActive"
          valuePropName="checked"
          initialValue={true}
        >
          <Switch />
        </Form.Item>

        <Form.Item
          label="Game ID"
          name="gameId"
          rules={[
            {
              required: true,
              message: "Please select a game",
            },
          ]}
        >
          <GameSelect />
        </Form.Item>

        <Form.Item
          label="Game Mode"
          name="gameMode"
        >
          <Input placeholder="Leave empty for Global" allowClear />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              <span>Formula</span>
              <Popover
                content={functionsPopoverContent}
                title="Available Functions"
                trigger="hover"
                placement="right"
              >
                <Button
                  type="text"
                  icon={<InfoCircleOutlined />}
                  size="small"
                  style={{ color: "#1890ff", padding: 0, height: "auto" }}
                />
              </Popover>
            </Space>
          }
          name="formula"
          rules={[
            {
              required: true,
              message: "Please enter a formula",
            },
          ]}
        >
          <TextArea rows={3} placeholder="Enter formula expression" />
        </Form.Item>

        <Form.Item>
          <Button
            onClick={handleValidateFormula}
            loading={validating}
            style={{ marginBottom: 8 }}
          >
            Validate Formula
          </Button>
          {validationResult && (
            <div style={{ marginTop: 8 }}>
              <Text
                style={{ color: validationResult.success ? "#1890ff" : "#ff4d4f" }}
              >
                {validationResult.message}
              </Text>
            </div>
          )}
        </Form.Item>

        <Form.Item
          label="Apply Min/Max Clamp"
          name="applyMinMaxClamp"
          valuePropName="checked"
          initialValue={false}
        >
          <Switch />
        </Form.Item>

        {applyMinMaxClamp && (
          <>
            <Form.Item
              label="Min Reward"
              name="minReward"
              rules={[
                {
                  required: applyMinMaxClamp,
                  message: "Please enter min reward",
                },
              ]}
            >
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>

            <Form.Item
              label="Max Reward"
              name="maxReward"
              rules={[
                {
                  required: applyMinMaxClamp,
                  message: "Please enter max reward",
                },
              ]}
            >
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </>
        )}

        <Form.Item
          label="Apply Balancer"
          name="applyBalancer"
          valuePropName="checked"
          initialValue={false}
        >
          <Switch />
        </Form.Item>

        {applyBalancer && (
          <Form.Item
            label="Balancer"
            name="balancer"
            rules={[
              {
                required: applyBalancer,
                message: "Please enter balancer value",
              },
            ]}
          >
            <InputNumber style={{ width: "100%" }} min={0} step={0.1} />
          </Form.Item>
        )}

        <Form.Item
          label="Apply Victory Bonus"
          name="applyVictoryBonus"
          valuePropName="checked"
          initialValue={false}
        >
          <Switch />
        </Form.Item>

        {applyVictoryBonus && (
          <Form.Item
            label="Victory Bonus Value"
            name="victoryBonusValue"
            rules={[
              {
                required: applyVictoryBonus,
                message: "Please enter victory bonus value",
              },
            ]}
          >
            <InputNumber style={{ width: "100%" }} min={0} step={0.1} />
          </Form.Item>
        )}

        <Form.Item
          label="Premium Booster Disabled"
          name="premiumBoosterDisabled"
          valuePropName="checked"
          initialValue={false}
        >
          <Switch />
        </Form.Item>

        <SubmitCancelButtons
          formId="createEditFormula"
          onClose={handleClose}
          isLoading={submitting || formulasStore.isLoading}
        />
      </Form>
    </Modal>
  )
})
